import { channelService } from '@repo/channel-talk';
import { excludeKeys } from '@repo/common-utils';
import { useUTM } from '@repo/platform';
import { useWorkingPlatform } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { useToast } from '@zenterprise-inc/ui';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { useMount } from 'react-use';

import { PromotionData, CorpTypeEnum } from '@/__generated__/searchRefundV2Mutation.graphql';
import { useSectionScrollModel } from '@/components/common/context/SectionScrollModelProvider';
import { 개인사업자고액환급액기준금액, 법인사업자고액환급액기준금액 } from '@/components/tax-refund/common/config/condition';
import { getCorporationRefundData, setCorporationRefundData } from '@/components/tax-refund/common/data/corporation-refund-data';
import { getIndisSearchRefundData, setIndisSearchRefundData } from '@/components/tax-refund/common/data/indis-search-refund-data';
import { getIndividualRefundData } from '@/components/tax-refund/common/data/individual-refund-data';
import { checkSearchStatus } from '@/components/tax-refund/common/graphql/query/checkSearchStatus';
import useProgressBar from '@/components/tax-refund/common/hooks/use-progress-bar';
import { getConsoleURLCorporation, getConsoleURLIndividual } from '@/components/tax-refund/common/utils/getConsoleURL';
import { getNaverPixelItems } from '@/components/tax-refund/common/utils/naver-pixel-items';
import NetworkError from '@/components/tax-refund/common/view/ErrorView/NetworkError';
import NoResponseError from '@/components/tax-refund/common/view/ErrorView/NoResponseError';
import LookupRequestView from '@/components/tax-refund/common/view/LookUpReqeustView';
import { OverflowControlModal } from '@/components/tax-refund/lookup/request/OverflowControlModal';
import { useRefundConversionData } from '@/components/tax-refund/lookup/result/apply-possible/use-apply-possible-refund-data';
import { CORPORATION_PATHS, INDIVIDUAL_PATHS } from '@/lib/constants/paths';
import useSearchRefund from '@/lib/hooks/api/mutation/use-search-refund';
import { useBznavRefundUserPhone } from '@/lib/hooks/api/query/use-bznav-refund-user-phone';
import { useAffiliateData } from '@/lib/hooks/use-affiliate';
import useNavigatorOnline from '@/lib/hooks/use-navigator-online';
import { useRandomWaitingModal } from '@/lib/hooks/use-random-waiting-modal';
import { refundTokenAtom } from '@/lib/stores/simple-auth';
import { BusinessType } from '@/lib/types/request';
import { handleErrorCode } from '@/lib/utils/handleErrorCode';
import { useInviteEventSessionData } from '@/lib/web/use-event';

const PAGE_TIMEOUT = 15 * 60 * 1000; // 15분
const INTERVAL_TIMEOUT = 3000; // 3초

export const LookupRefundRequestContent = ({
  businessType,
  handleShowNetworkDisconnection
}: {
  businessType: BusinessType;
  handleShowNetworkDisconnection: (isShow: boolean) => void;
}) => {
  const router = useRouter();
  const toast = useToast();
  const isIndis = businessType === 'indis';

  const { commit: searchRefundRequest, isInFlight: isSearchRefundRequestLoading } = useSearchRefund();
  const { eventAttribute } = useSectionScrollModel();
  const { workingPlatform } = useWorkingPlatform();
  const { isInitWaitingModalSetting, waitingTime, setWaitingTime, showWaitingModal, waitingCallback, setWaitingCallback } =
    useRandomWaitingModal('OVERFLOW_WAITING_TIME');
  const { sendViewEvent, sendClickEvent, sendCustomActionEvent, sendMetaEvent, sendKakaoEvent, sendNaverEvent } = useUserEventLogger();
  const { getConversionData } = useRefundConversionData();

  const { utmData } = useUTM();
  const relayEnvironment = useRelayEnvironment();
  const invitePromotionData = useInviteEventSessionData();
  const isOnline = useNavigatorOnline();
  const { percent, start, done, stop, restart } = useProgressBar(85, 95, isIndis ? 51000 : 76000);
  const affiliateData = useAffiliateData();

  const hasPhoneNumber = !!useBznavRefundUserPhone();

  const refundToken = useAtomValue(refundTokenAtom);
  const [showNetworkDisconnection, setShowNetworkDisconnection] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);
  const [showRefresh, setShowRefresh] = useState(false);

  const intervalTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const retryCount = useRef(0);
  const isLookupRequestStarted = useRef(false);

  const handleRetry = () => {
    sendClickEvent(isIndis ? 'indis-lookup_loading_offline-retry' : 'corps-lookup_loading_offline-retry');
    showWaitingModal();
    restart();
  };

  const stopCheckSearchStatus = () => {
    if (intervalTimer.current) {
      clearInterval(intervalTimer.current);
      intervalTimer.current = null;
      isLookupRequestStarted.current = false;
    }
  };

  const lookupCompleteAction = useCallback(() => {
    const indisSearchRefundData = getIndisSearchRefundData();
    const corpsSearchRefundData = getCorporationRefundData();
    const { totalAmount, incomeAmount, tritxAmount, refundType, refundGrade, products } = getConversionData(
      businessType,
      businessType === 'indis' ? indisSearchRefundData?.refundAmount : corpsSearchRefundData?.refundAmount,
      businessType === 'indis' ? indisSearchRefundData?.refundStatus : corpsSearchRefundData?.refundStatus,
      indisSearchRefundData?.tritxRefundInfo?.refundAmount,
      indisSearchRefundData?.tritxRefundInfo?.refundStatus
    );

    sendViewEvent(
      'lookup_result',
      {
        ...excludeKeys(eventAttribute ?? {}, ['current_section']),
        businessType,
        amount: totalAmount,
        incomeAmount,
        tritxAmount,
        grade: refundGrade,
        type: refundType
      },
      {
        isUseAirbridge: true,
        isUseGoogleAnalytics: true
      }
    );

    sendCustomActionEvent(
      'InitiateCheckout',
      '',
      {
        businessType,
        amount: totalAmount
      },
      { isUseTiktokPixel: true, isUseMixpanel: false }
    );
    sendMetaEvent('CompleteRegistration', {
      contents_category: businessType,
      contents_type: 'product',
      contents_name: refundType,
      user_grade: refundGrade,
      value: totalAmount,
      value_income: incomeAmount,
      value_gains: tritxAmount
    });
    sendKakaoEvent('addToCart', {
      currency: 'KRW',
      tag: refundType,
      products
    });
    sendNaverEvent({
      type: 'add_to_cart',
      id: businessType === 'indis' ? indisSearchRefundData?.refundId : corpsSearchRefundData?.refundId,
      value: String(totalAmount),
      currency: 'KRW',
      items: getNaverPixelItems(businessType, incomeAmount, tritxAmount)
    });

    router.replace({
      pathname: isIndis ? INDIVIDUAL_PATHS.개인환급액조회결과 : CORPORATION_PATHS.법인환급액조회결과,
      query: { type: businessType }
    });
  }, [businessType, eventAttribute, getConversionData, isIndis, router, sendCustomActionEvent, sendKakaoEvent, sendMetaEvent, sendNaverEvent, sendViewEvent]);

  const requestLookup = useCallback(() => {
    const refundInquiryRequestData = isIndis ? getIndividualRefundData() : getCorporationRefundData();
    if (isSearchRefundRequestLoading) return;
    const searchRefundData = isIndis ? getIndisSearchRefundData() : undefined;
    isLookupRequestStarted.current = true;

    searchRefundRequest({
      variables: {
        corpType: businessType.toUpperCase() as CorpTypeEnum,
        refundToken: refundToken as string,
        regNo: isIndis ? refundInquiryRequestData?.regNo : undefined,
        clientId: `bznav-refund-${workingPlatform}`,
        reSearchRequestId: searchRefundData?.reSearchRequestId ?? null,
        tritxReSearchRequestId: searchRefundData?.tritxRefundInfo?.tritxReSearchRequestId ?? null,
        promotionData: invitePromotionData.get() as PromotionData,
        optionalTerms: refundInquiryRequestData?.serviceAgreementFlow
          ? { marketingAgree: !!refundInquiryRequestData?.marketingAgree, thirdAgree: !!refundInquiryRequestData?.thirdAgree }
          : null,
        ...utmData,
        affiliateData:
          affiliateData && affiliateData.trackingId
            ? {
                name: affiliateData.name,
                ...(affiliateData.appKey ? { appKey: affiliateData.appKey } : {}),
                trackingId: affiliateData.trackingId
              }
            : undefined
      },
      onCompleted: (data, error) => {
        const { errors } = data?.searchRefundV2 || {};
        if (error || errors?.code) {
          stop();

          if (errors?.code) {
            router.replace({
              pathname: handleErrorCode(businessType, errors?.code),
              query: router.query
            });
          } else if (error) {
            console.error(error);
            toast({ content: error[0]?.message, icon: 'error', iconColor: 'red.600' });
          }
          return;
        }
      },
      onError: () => {
        if (retryCount.current >= 5) {
          router.replace(isIndis ? INDIVIDUAL_PATHS.개인기타오류 : CORPORATION_PATHS.법인기타오류);
          return;
        }

        setTimeout(() => {
          retryCount.current += 1;
          requestLookup();
        }, 1000);
      }
    });
  }, [
    affiliateData,
    businessType,
    invitePromotionData,
    isIndis,
    isSearchRefundRequestLoading,
    refundToken,
    router,
    searchRefundRequest,
    stop,
    toast,
    utmData,
    workingPlatform
  ]);

  const startLookupRequest = useCallback(() => {
    intervalTimer.current = setInterval(() => {
      checkSearchStatus(relayEnvironment, businessType.toUpperCase() as CorpTypeEnum).then((res) => {
        const { refundInfo, tritxRefundInfo, errors, result, progress } = res || {};
        if (!progress && !isLookupRequestStarted.current) {
          requestLookup();
          start();
          return;
        }

        if (errors?.code) {
          router.replace(handleErrorCode(businessType, errors?.code));
        } else if (!progress) {
          stop();
          stopCheckSearchStatus();
          setShowRefresh(true);
        }

        if (result && progress === '100') {
          stopCheckSearchStatus();
          if (!refundInfo) return;
          const vipStandardAmount = isIndis ? 개인사업자고액환급액기준금액 : 법인사업자고액환급액기준금액;

          if (isIndis) {
            setIndisSearchRefundData({
              refundId: refundInfo.refundId,
              refundStatus: refundInfo.refundStatus,
              refundAmount: refundInfo.refundAmount,
              bizName: refundInfo.businessInfo?.taxpayerName ?? '',
              snmcSpcTxreAmt: refundInfo.snmcSpcTxreAmt ?? 0,
              cenSnmcTxreAmt: refundInfo.cenSnmcTxreAmt ?? 0,
              empIncrEtrpTxamtDdcAmt: refundInfo.empIncrEtrpTxamtDdcAmt ?? 0,
              intgEmpTxamtDdcAmt: refundInfo.intgEmpTxamtDdcAmt ?? 0,
              sctyInfeeTxamtDdcAmt: refundInfo.sctyInfeeTxamtDdcAmt ?? 0,
              reSearchRequestId: refundInfo.reSearchRequestId ?? null,
              tritxRefundInfo:
                workingPlatform !== 'toss' && tritxRefundInfo
                  ? {
                      bizName: tritxRefundInfo.businessInfo?.taxpayerName ?? '',
                      refundAmount: tritxRefundInfo.refundAmount,
                      refundId: tritxRefundInfo.refundId,
                      refundStatus: tritxRefundInfo.refundStatus,
                      tritxReSearchRequestId: tritxRefundInfo.tritxReSearchRequestId ?? null,
                      businessInfo: {
                        taxpayerName: tritxRefundInfo.businessInfo?.taxpayerName ?? ''
                      }
                    }
                  : undefined
            });
          } else {
            const refundData = getCorporationRefundData();
            if (refundData) {
              refundData.refundId = refundInfo.refundId;
              refundData.refundAmount = refundInfo.refundAmount;
              refundData.bizName = refundInfo.businessInfo?.hometaxBizName ?? '';
              refundData.refundStatus = refundInfo.refundStatus;
              setCorporationRefundData(refundData);
            }
          }

          channelService.updateUser({
            profile: {
              refundServiceType: isIndis ? '개인' : '법인',
              refundTotalAmount: refundInfo.refundAmount,
              refundVIP: Number(refundInfo.refundAmount) >= vipStandardAmount,
              refundConsoleURL: isIndis ? getConsoleURLIndividual(refundInfo?.refundId) : getConsoleURLCorporation(refundInfo?.refundId)
            }
          });
          done(lookupCompleteAction);
        }
      });
    }, INTERVAL_TIMEOUT);
  }, [relayEnvironment, businessType, requestLookup, start, router, stop, isIndis, done, lookupCompleteAction, workingPlatform]);

  useMount(() => {
    if (isIndis) {
      setWaitingCallback(startLookupRequest);
    } else {
      startLookupRequest();
    }

    return () => {
      stop();
      stopCheckSearchStatus();
    };
  });

  useEffect(() => {
    if (!isOnline) {
      stop();
      stopCheckSearchStatus();
    }
    setDisabledButton(!isOnline);
    setShowNetworkDisconnection(!isOnline);
    handleShowNetworkDisconnection(!isOnline);
  }, [handleShowNetworkDisconnection, isOnline, stop]);

  useEffect(() => {
    if (isInitWaitingModalSetting && !isLookupRequestStarted.current) {
      showWaitingModal();
    }
  }, [showWaitingModal, isInitWaitingModalSetting]);

  useEffect(() => {
    const timer = setTimeout(() => {
      router.replace(isIndis ? INDIVIDUAL_PATHS.개인홈택스수집지연 : CORPORATION_PATHS.법인홈택스수집지연);
    }, PAGE_TIMEOUT);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isIndis, router]);

  if (showRefresh) {
    return <NoResponseError />;
  }

  return (
    <>
      {showNetworkDisconnection ? (
        <NetworkError disabledPrimaryButton={disabledButton} onPrimaryButtonClick={handleRetry} />
      ) : (
        <>
          <LookupRequestView businessType={businessType} percent={percent} hasPhoneNumber={hasPhoneNumber} />
          {isIndis && waitingTime > 0 && <OverflowControlModal waitingTime={waitingTime} setWaitingTime={setWaitingTime} callbackFunction={waitingCallback} />}
        </>
      )}
    </>
  );
};
